import { ENTERPRISE_PARTNER_ID } from '@brighte/enterprise-common/constants';
import { BoxProps } from '@spark-web/box';
import { useTheme } from '@spark-web/theme';
import { CSSProperties, useMemo } from 'react';
import { useOutletContext } from 'react-router';

import { OutletContextType } from '~/types/marketplace';

type PartnerTheme = {
  background: string;
  card: Partial<BoxProps>;
  tooltip: Partial<BoxProps> & { tone: string; closeIconPresent?: boolean };
};

export const usePartnerTheming = (): PartnerTheme => {
  const { partner } = useOutletContext<OutletContextType>();
  const theme = useTheme();

  const baseTooltipStyle = useMemo(
    () => ({
      width: 'auto',
      maxWidth: '350px',
      top: 30,
    }),
    [],
  );

  const createTooltipArrow = (color: string) => ({
    content: "''",
    position: 'absolute' as CSSProperties['position'],
    top: -10,
    transform: 'translateX(-45%)',
    borderLeft: '10px solid transparent',
    borderRight: '10px solid transparent',
    borderBottom: `10px solid ${color}`,
  });

  const styles: PartnerTheme = useMemo(() => {
    const getDefaultTooltipStyles = (backgroundColor: string, arrowColor: string) => ({
      ...baseTooltipStyle,
      backgroundColor,
      '&::before': createTooltipArrow(arrowColor),
    });

    switch (partner) {
      case ENTERPRISE_PARTNER_ID.commbank:
        return {
          background: '#F4F4F4',
          card: {
            borderRadius: 'none',
            borderLeft: 'fieldAccent',
            css: {
              boxShadow: '0px 0px 3px 0px rgba(0, 0, 0, 0.20)',
            },
          },
          tooltip: {
            tone: 'neutralInverted',
            closeIconPresent: true,
            css: getDefaultTooltipStyles('#000000', '#000000'),
          },
        };

      default:
        return {
          background: '#F1F4FF',
          card: {
            borderRadius: 'large',
          },
          tooltip: {
            tone: 'neutral',
            borderRadius: 'small',
            border: 'fieldHover',
            closeIconPresent: false,
            css: getDefaultTooltipStyles(theme.color.background.neutral, theme.border.color.fieldHover),
          },
        };
    }
  }, [partner, theme, baseTooltipStyle]);

  return styles;
};
